/**
 * RollbackWoBadge + RollbackConfirmDialog (ticket-07)
 *
 * RollbackWoBadge: red badge for Rollback Work Orders (wo_type === "ROLLBACK").
 * Shows a tooltip with the link to the original cancelled WO.
 *
 * RollbackConfirmDialog: shown before CANCEL when the WO has DONE items.
 * Two modes:
 *   - withDone=true  → "Будет создан Наряд-откат для N выполненных шагов. Продолжить?"
 *   - withDone=false → simple "Подтвердите отмену наряда"
 */

import { useState } from 'react'
import { cancelWarning } from './woLifecycle.logic'

// ─── RollbackWoBadge ──────────────────────────────────────────────────────────

interface RollbackWoBadgeProps {
  parentWorkOrderId: string
  className?: string
}

export function RollbackWoBadge({ parentWorkOrderId, className = '' }: RollbackWoBadgeProps) {
  const [showTooltip, setShowTooltip] = useState(false)
  const short = parentWorkOrderId.slice(0, 8)

  return (
    <span className={`relative inline-flex items-center ${className}`}>
      <button
        type="button"
        aria-label={`Наряд-откат. Оригинал отменён: ${parentWorkOrderId}`}
        onClick={() => setShowTooltip((v) => !v)}
        onBlur={() => setShowTooltip(false)}
        className="inline-flex items-center gap-1 rounded px-2 py-0.5 text-xs font-medium
                   bg-red-500/15 text-red-400 border border-red-500/25
                   hover:bg-red-500/25 transition-colors cursor-help"
      >
        {/* Undo/arrow-path icon */}
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 16 16" fill="currentColor"
          aria-hidden="true" className="w-3 h-3">
          <path fillRule="evenodd"
            d="M8 3.5c-.771 0-1.537.022-2.3.064l.756.755a.75.75 0 0 1-1.06 1.06l-2.06-2.06a.75.75 0 0 1 0-1.061l2.06-2.06a.75.75 0 0 1 1.06 1.06l-.66.66C6.647 1.52 7.318 1.5 8 1.5a6.5 6.5 0 1 1-4.965 2.298.75.75 0 1 1 1.144.97A5 5 0 1 0 8 3.5Z"
            clipRule="evenodd" />
        </svg>
        Наряд-откат
      </button>

      {showTooltip && (
        <div role="tooltip"
          className="absolute left-0 top-full mt-1 z-50 w-72 rounded border border-border
                     bg-bg-panel shadow-lg text-xs text-text-secondary p-3">
          <p className="font-semibold text-text-primary mb-1">Наряд-откат</p>
          <p>
            Создан автоматически при отмене наряда{' '}
            <span className="font-mono text-red-300">{short}…</span>.
            Содержит обратные операции для выполненных шагов.
          </p>
        </div>
      )}
    </span>
  )
}


// ─── RollbackConfirmDialog ────────────────────────────────────────────────────

interface RollbackConfirmDialogProps {
  /** Number of DONE items in the WO being cancelled */
  doneItemCount: number
  woTitle: string
  onConfirm: () => void
  onCancel: () => void
  isLoading?: boolean
}

export function RollbackConfirmDialog({
  doneItemCount,
  woTitle,
  onConfirm,
  onCancel,
  isLoading = false,
}: RollbackConfirmDialogProps) {
  const withDone = doneItemCount > 0
  const warning = cancelWarning(doneItemCount)

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="cancel-dialog-title"
      className="fixed inset-0 z-[200] flex items-center justify-center bg-black/60 backdrop-blur-sm"
    >
      <div className="bg-bg-panel border border-border rounded-xl shadow-2xl p-6 max-w-md w-full mx-4">
        {/* Icon */}
        <div className={`w-12 h-12 rounded-full flex items-center justify-center mb-4
          ${withDone ? 'bg-red-500/15' : 'bg-orange-500/15'}`}>
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor"
            className={`w-6 h-6 ${withDone ? 'text-red-400' : 'text-orange-400'}`}
            aria-hidden="true">
            <path fillRule="evenodd"
              d="M9.401 3.003c1.155-2 4.043-2 5.197 0l7.355 12.748c1.154 2-.29 4.5-2.599 4.5H4.645c-2.309 0-3.752-2.5-2.598-4.5L9.4 3.003ZM12 8.25a.75.75 0 0 1 .75.75v3.75a.75.75 0 0 1-1.5 0V9a.75.75 0 0 1 .75-.75Zm0 8.25a.75.75 0 1 1 0-1.5.75.75 0 0 1 0 1.5Z"
              clipRule="evenodd" />
          </svg>
        </div>

        <h2 id="cancel-dialog-title" className="text-lg font-semibold text-text-primary mb-2">
          {warning.title}
        </h2>

        <p className="text-sm text-text-secondary mb-1">
          <span className="font-medium text-text-primary">{woTitle}</span>
        </p>

        {withDone ? (
          <div className="mt-3 rounded-lg border border-red-500/20 bg-red-500/5 p-3 text-sm text-red-300">
            <p className="font-medium mb-1">⚠ Будет создан Наряд-откат</p>
            <p>{warning.body}</p>
          </div>
        ) : (
          <p className="mt-2 text-sm text-text-secondary">{warning.body}</p>
        )}

        <div className="mt-6 flex gap-3 justify-end">
          <button
            onClick={onCancel}
            disabled={isLoading}
            className="px-4 py-2 text-sm rounded-lg border border-border text-text-secondary
                       hover:text-text-primary hover:border-border-hover transition-colors
                       disabled:opacity-50"
          >
            Назад
          </button>
          <button
            onClick={onConfirm}
            disabled={isLoading}
            className={`px-4 py-2 text-sm rounded-lg font-medium transition-colors
              disabled:opacity-50
              ${withDone
                ? 'bg-red-500/20 text-red-300 border border-red-500/30 hover:bg-red-500/30'
                : 'bg-orange-500/20 text-orange-300 border border-orange-500/30 hover:bg-orange-500/30'
              }`}
          >
            {isLoading ? 'Отмена…' : warning.confirmLabel}
          </button>
        </div>
      </div>
    </div>
  )
}

export default RollbackWoBadge
